import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import BaseDialog from 'core/components/base-dialog/base-dialog';
import { Spinner } from 'core/components/spinner';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import { useToast } from 'core/components/toast';
import { RenameDocumentModal } from 'core/components/rename-document-modal';
import reviewImagesIcon from 'core/assets/svg/ic-review-images.svg';
import { DocumentPreviewCard } from './document-preview-card';
import { isRenderableImage } from './is-renderable-image';

import useStyles from './document-image-preview-modal.styles';

/**
 * Read-only image gallery over a set of uploaded documents (issue #31493).
 * Lets operators preview every renderable image at once, open them all in new
 * tabs, and rename them via the shared rename modal — instead of cross-referencing
 * the docs table one file at a time. Only browser-renderable images appear here;
 * other files stay table-only.
 *
 * Context-agnostic: every account/offer-specific concern is injected via props
 * (`getSignedUrl`, `onRename`, `canRenameDocument`, `getDocName`, `title`), so the
 * same gallery serves both the customer Internal Documents and the offer Supporting
 * Documents surfaces.
 */
const DocumentImagePreviewModal = observer(
  ({ open, onClose, documents, getDocName, getSignedUrl, onRename, canRenameDocument, title }) => {
    const classes = useStyles();
    const toast = useToast();

    const imageDocs = useMemo(() => documents.filter((doc) => isRenderableImage(doc.path)), [documents]);
    // Signed URLs are resolved once per path and kept in a ref, so a rename
    // refetch (which hands back a new documents array with the same paths)
    // doesn't re-sign every image and make the whole gallery flicker.
    const signedUrlsRef = useRef({});
    const [urlsByPath, setUrlsByPath] = useState(null);
    const [documentToRename, setDocumentToRename] = useState(null);

    useEffect(() => {
      if (!open) {
        // Reset so a fresh open re-signs (signed URLs are short-lived).
        signedUrlsRef.current = {};
        setUrlsByPath(null);
        return undefined;
      }
      let cancelled = false;
      (async () => {
        // Only sign paths we haven't signed yet this session.
        const unsigned = imageDocs.filter((doc) => !(doc.path in signedUrlsRef.current));
        try {
          if (unsigned.length) {
            const entries = await Promise.all(unsigned.map(async (doc) => [doc.path, await getSignedUrl(doc.path)]));
            signedUrlsRef.current = { ...signedUrlsRef.current, ...Object.fromEntries(entries) };
          }
          if (!cancelled) {
            setUrlsByPath(signedUrlsRef.current);
          }
        } catch {
          if (!cancelled) {
            // Clear the spinner instead of hanging forever, and tell the user.
            setUrlsByPath(signedUrlsRef.current);
            toast.notify({
              type: 'error',
              message: "Some images couldn't be loaded. Close and reopen the preview to try again."
            });
          }
        }
      })();
      return () => {
        cancelled = true;
      };
    }, [open, imageDocs, getSignedUrl, toast]);

    const loading = !urlsByPath;

    const handleOpenAllImages = () => {
      imageDocs.forEach((doc) => {
        window.open(urlsByPath[doc.path], '_blank');
      });
      toast.notify({
        type: 'success',
        message: 'All of the images have opened in new tabs in your browser. To view them, navigate to each new tab.'
      });
    };

    return (
      <>
        <BaseDialog open={open} onClose={onClose} className={classes.container}>
          <Grid container className={classes.header} justifyContent="space-between" alignItems="center">
            <Grid item className={classes.titleContainer}>
              <Label type="greenBig" className={classes.modalHeader}>
                {title}
              </Label>
              <div className={classes.totalContainer}>
                <Typography variant="subtitle2" className={classes.totalWord}>
                  TOTAL
                </Typography>
                <Typography variant="subtitle2" className={classes.totalValue}>
                  {imageDocs.length} {imageDocs.length === 1 ? 'image' : 'images'}
                </Typography>
              </div>
            </Grid>
            <Grid item>
              <Button
                disableFocusRipple
                className={classes.openAllImagesButton}
                onClick={handleOpenAllImages}
                disabled={loading}
                variant="text"
                color="secondary"
              >
                Open all images
                <img alt="review images icon" className={classes.reviewImagesIcon} src={reviewImagesIcon} />
              </Button>
            </Grid>
          </Grid>

          {loading ? (
            <div className={classes.loadingContainer}>
              <Spinner size={102} />
            </div>
          ) : (
            <Grid container spacing={4}>
              {imageDocs.map((doc) => (
                <Grid item key={doc.path} xs={12} sm={6} md={3}>
                  <DocumentPreviewCard
                    doc={doc}
                    fileName={getDocName(doc.path)}
                    imageUrl={urlsByPath[doc.path]}
                    canRename={canRenameDocument(doc)}
                    onRename={() => setDocumentToRename(doc)}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          <Grid className={classes.btnContainer}>
            <Button variant="contained" color="primary" className={classes.closeBtn} onClick={onClose}>
              Close
            </Button>
          </Grid>
        </BaseDialog>
        {documentToRename && (
          <RenameDocumentModal
            fileName={getDocName(documentToRename.path)}
            currentLabel={documentToRename.displayName || ''}
            onSave={(label) => onRename(documentToRename.path, label)}
            onClose={() => setDocumentToRename(null)}
          />
        )}
      </>
    );
  }
);

DocumentImagePreviewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string.isRequired,
      lastModified: PropTypes.string,
      displayName: PropTypes.string
    })
  ).isRequired,
  getDocName: PropTypes.func.isRequired,
  getSignedUrl: PropTypes.func.isRequired,
  onRename: PropTypes.func.isRequired,
  canRenameDocument: PropTypes.func,
  title: PropTypes.string.isRequired
};

DocumentImagePreviewModal.defaultProps = {
  canRenameDocument: () => false
};

export default DocumentImagePreviewModal;
